// src/features/quality/pages/InspectionRecordCreatePage.tsx

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EntityPicker } from "@/shared/components/EntityPicker";
import { PageBanner } from "@/shared/components/PageBanner";
import { useV1QualityCheckRevisionsList, useV1InspectionsCreate } from "@/api/generated/v1/v1";
import type {
  InspectionResultValue,
  InspectionTargetType,
  QualityCheckRevision,
} from "../types";

const TARGET_TYPE_TO_MODEL: Record<InspectionTargetType, string> = {
  production_order: "production.productionorder",
  stock_transaction: "inventory.stocktransaction",
};

export function InspectionRecordCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedRevisionId = searchParams.get("revision") ?? "";

  const [revisionId, setRevisionId] = useState(preselectedRevisionId);
  const [targetType, setTargetType] = useState<InspectionTargetType>("production_order");
  const [target, setTarget] = useState<{ id: string; display: string } | null>(null);
  const [result, setResult] = useState<InspectionResultValue>("pass");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: revisionsResponse } = useV1QualityCheckRevisionsList({
    approval_state: "approved",
  } as any);
  const publishedRevisions: QualityCheckRevision[] = revisionsResponse?.results ?? [];

  const createMutation = useV1InspectionsCreate();

  async function handleSubmit() {
    setError(null);
    if (!revisionId) {
      setError("انتخاب برنامهٔ کنترل کیفیت الزامی است.");
      return;
    }
    if (!target) {
      setError("هدف بازرسی را انتخاب کنید.");
      return;
    }
    try {
      await createMutation.mutateAsync({
        data: {
          quality_check_revision: revisionId,
          target_type: targetType,
          target_id: target.id,
          result,
          notes,
        } as any,
      });
      navigate("/quality/inspections");
    } catch (err: any) {
      const backendError =
        err?.response?.data?.quality_check_revision?.[0] ??
        err?.response?.data?.target_type?.[0] ??
        err?.response?.data?.non_field_errors?.[0] ??
        "خطا در ثبت بازرسی.";
      setError(backendError);
    }
  }

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <PageBanner
        title="ثبت بازرسی کیفیت جدید"
        subtitle="ثبت نتیجه بازرسی کیفی برای سفارش تولید یا تراکنش انبار"
        icon="🔍"
        helpLink="/help/quality/inspection-record-create"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1.1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            ← انصراف
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            style={{
              padding: "0.6rem 1.3rem",
              borderRadius: "12px",
              background: "#ffffff",
              color: "#007270",
              fontWeight: 700,
              fontSize: "0.9rem",
              border: "none",
              cursor: createMutation.isPending ? "not-allowed" : "pointer",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>💾</span>
            <span>{createMutation.isPending ? "در حال ثبت..." : "ثبت نهایی نتیجه"}</span>
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.75rem" }}>
        <div
          style={{
            paddingBottom: "1rem",
            marginBottom: "1.5rem",
            borderBottom: "1px solid var(--color-border, #E4DCC8)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span style={{ fontSize: "1.2rem" }}>📋</span>
          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>
            مشخصات و ارزیابی بازرسی کیفی
          </h3>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div>
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "block",
                marginBottom: "6px",
              }}
            >
              برنامه کنترل کیفیت (Revision منتشرشده) <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <select
              value={revisionId}
              onChange={(e) => setRevisionId(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "#2B2B2B",
                fontSize: "0.9rem",
                outline: "none",
              }}
            >
              <option value="">— انتخاب کنید —</option>
              {publishedRevisions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — Rev {r.revision_no}
                </option>
              ))}
            </select>
            {publishedRevisions.length === 0 && (
              <p style={{ fontSize: "0.8rem", color: "#EF4444", marginTop: "0.4rem", fontWeight: 600 }}>
                ⚠️ هیچ برنامه تایید شده‌ای موجود نیست.
              </p>
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.25rem",
            }}
          >
            <div>
              <label
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#2B2B2B",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                نوع هدف ارزیابی <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <select
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value as InspectionTargetType);
                  setTarget(null);
                }}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  border: "1px solid var(--color-border, #E4DCC8)",
                  background: "#FFFFFF",
                  color: "#2B2B2B",
                  fontSize: "0.9rem",
                  outline: "none",
                }}
              >
                <option value="production_order">سفارش تولید</option>
                <option value="stock_transaction">تراکنش موجودی</option>
              </select>
            </div>

            <div>
              <label
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#2B2B2B",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                موجودیت هدف بازرسی <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <EntityPicker
                model={TARGET_TYPE_TO_MODEL[targetType]}
                value={target}
                onChange={setTarget}
                placeholder="انتخاب یا جستجوی هدف بازرسی..."
              />
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "block",
                marginBottom: "6px",
              }}
            >
              نتیجه نهایی بازرسی <span style={{ color: "#EF4444" }}>*</span>
            </label>
            <select
              value={result}
              onChange={(e) => setResult(e.target.value as InspectionResultValue)}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: `2px solid ${result === "pass" ? "#10B981" : "#EF4444"}`,
                background: result === "pass" ? "rgba(16, 185, 129, 0.04)" : "rgba(239, 68, 68, 0.04)",
                color: result === "pass" ? "#065F46" : "#991B1B",
                fontWeight: 800,
                fontSize: "0.95rem",
                outline: "none",
              }}
            >
              <option value="pass">✅ قبول (Pass)</option>
              <option value="fail">❌ رد (Fail)</option>
            </select>
          </div>

          <div>
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#2B2B2B",
                display: "block",
                marginBottom: "6px",
              }}
            >
              توضیحات و گزارش ناظر کیفی
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="در صورت وجود مشکل، عدم تطابق یا توضیحات تکمیلی، گزارش را در اینجا ثبت کنید..."
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "#FFFFFF",
                color: "#2B2B2B",
                fontSize: "0.9rem",
                outline: "none",
                resize: "vertical",
                minHeight: "100px",
              }}
            />
          </div>

          {error && (
            <div
              style={{
                padding: "0.85rem 1.1rem",
                background: "rgba(239, 68, 68, 0.12)",
                border: "1px solid #EF4444",
                color: "#B91C1C",
                borderRadius: "10px",
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            >
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InspectionRecordCreatePage;
